/**
 * Execute 域 — API 接口
 * 
 * 对接后端：
 * - /tasks/today, /tasks/{id}/complete, /tasks/streak
 * - /longevity/plans, /longevity/plans/{id}/days/{day}
 */

import { shouldUseMock } from '../../api/config';
import { http } from '../../api/http';
import type {
  AbilityLedgerResponse,
  CycleReviewResponse,
  DailyBrief,
  FeedbackCompletionPayload,
  FeedbackType,
  LongevityGoalCard,
  LongevityPlan,
  LongevityPracticeSummary,
  PracticeTask,
  RiskTriageResult,
  WeeklyPracticePlan,
  WorkoutSessionDetail,
  WorkoutSessionSummary,
} from './longevity';
import {
  getGoalCardById,
  longevityGoalCards,
  mockDailyBrief,
  mockLongevityPlan,
} from './longevity';

// ── 类型定义 ──

export interface InterventionTask {
  id: string;
  title: string;
  description: string;
  tag: string;
  completed: boolean;
  courseId?: string;
  completionMode?: 'instant' | 'input';
  inputUnit?: string | null;
}

export interface DailyPlan {
  date: string;
  tasks: InterventionTask[];
  completedCount: number;
  totalCount: number;
}

export interface TaskStreak {
  currentStreak: number;
  longestStreak: number;
}

/** 微光积累数据 */
export interface ShimmerData {
  weeklyRate: number;
  weeklyCompleted: number;
  weeklyTotal: number;
  monthlyRate: number;
  currentStreak: number;
  waterLevel: number;
  waterLevelLabel: string;
}

/** 执行计划 */
export interface ExecutionPlan {
  id: string;
  title: string;
  description?: string;
  planType: string;
  status: 'active' | 'paused' | 'completed';
  targetDays: number;
  currentDay: number;
  progress: { completed: number; total: number; percent: number };
}

/** 靶向逆转协议 */
export interface Protocol {
  id: string;
  title: string;
  condition: string;
  durationDays: number;
  description: string;
}

/** 食谱 */
export interface Recipe {
  id: string;
  title: string;
  category: string;
  description: string;
  imageUrl?: string;
  favorited?: boolean;
}

export interface LongevityPlanPreview {
  riskTriage: RiskTriageResult;
  goalCards: LongevityGoalCard[];
  evidenceBoundary: DailyBrief['evidenceBoundary'];
  durationDays: number;
  dailyBrief: DailyBrief | null;
}

export interface LongevityAudio {
  status: DailyBrief['artifactStatuses']['podcastAudio'];
  url?: string | null;
  durationSec?: number | null;
  provider?: string | null;
  format?: string | null;
  updatedAt?: string | null;
  error?: string | null;
}

export interface LongevityDayPackage {
  plan: LongevityPlan;
  dayId: string;
  dayNumber: number;
  status: string;
  dailyBrief: DailyBrief;
  audio: LongevityAudio;
  feedbackEvents: Array<{
    id: string;
    taskId: string;
    feedbackType: FeedbackType;
    note?: string | null;
    completionPayload?: FeedbackCompletionPayload | Record<string, unknown> | null;
    createdAt: string;
  }>;
}

export interface LongevityFeedbackResponse {
  event: LongevityDayPackage['feedbackEvents'][number];
  taskStatus: PracticeTask['status'];
  dayStatus: string;
}

export interface WorkoutSessionQuery {
  from?: string;
  to?: string;
  limit?: number;
  activityType?: string;
}

export interface LinkPracticeEvidenceRequest {
  evidenceType: 'workout_session' | 'manual_note';
  workoutId?: string | null;
  note?: string | null;
  occurredAt?: string | null;
  completeTask?: boolean;
  idempotencyKey?: string;
}

export interface LinkPracticeEvidenceResponse {
  eventId: string;
  taskId: string;
  planId: string;
  planDayId: string;
  evidenceType: string;
  evidenceRefId?: string | null;
  taskStatus: PracticeTask['status'];
  dayStatus: string;
  summary: string;
}

export interface CycleRolloverRequest {
  goalCardIds?: string[];
  personalMotives?: Record<string, string>;
  concern?: string | null;
  durationDays?: number;
}

export interface CycleRolloverResponse {
  previousPlanId: string;
  nextPlanId: string;
  dayPackage: LongevityDayPackage;
  summary: string;
}

export interface GenerateLongevityAudioResponse {
  dayId: string;
  dayNumber: number;
  audio: LongevityAudio;
}

export interface LongevityDaySummary {
  dayId: string;
  dayNumber: number;
  status: string;
  title: string;
  primaryGoalCardId: string;
  taskTitle: string;
  taskDoseLabel: string;
  artifactStatuses: DailyBrief['artifactStatuses'];
  latestFeedbackType?: FeedbackType | null;
  completedAt?: string | null;
}

export interface ProgressDecision {
  id: string;
  decisionType: string;
  decisionPayload: Record<string, unknown>;
  requiresUserConfirmation: boolean;
  status: string;
  createdAt: string;
}

export interface LongevityPlanDaysResponse {
  plan: LongevityPlan;
  days: LongevityDaySummary[];
  progressDecisions: ProgressDecision[];
}

export interface ShareGoalResponse {
  id: string;
  status: string;
  visibleFields: string[];
  allowReshare: boolean;
  expiresAt?: string | null;
  createdAt: string;
  privacyBoundary: string;
}

function mockPlanForGoals(goalCardIds = mockLongevityPlan.selectedGoalCardIds): LongevityPlan {
  return {
    ...mockLongevityPlan,
    selectedGoalCardIds: goalCardIds,
    goals: goalCardIds.map((goalCardId, index) => ({
      id: `mock-goal-${goalCardId}`,
      goalCardId,
      role: index === 0 ? 'primary' : 'vision',
      rank: index + 1,
      card: getGoalCardById(goalCardId) ?? null,
    })),
  };
}

function mockBriefForGoals(goalCardIds = mockLongevityPlan.selectedGoalCardIds, day = 1): DailyBrief {
  const primaryGoalCardId = goalCardIds[0] ?? mockDailyBrief.primaryGoalCardId;
  const primaryGoal = getGoalCardById(primaryGoalCardId);
  return {
    ...mockDailyBrief,
    day,
    title: primaryGoal
      ? `为什么当前先服务“${primaryGoal.title}”`
      : mockDailyBrief.title,
    primaryGoalCardId,
    goalCardIds,
    practiceTask: {
      ...mockDailyBrief.practiceTask,
      goalCardId: primaryGoalCardId,
    },
  };
}

function mockLongevityDayPackage(goalCardIds = mockLongevityPlan.selectedGoalCardIds, day = 1): LongevityDayPackage {
  const dailyBrief = mockBriefForGoals(goalCardIds, day);
  return {
    plan: mockPlanForGoals(goalCardIds),
    dayId: `mock-longevity-day-${day}`,
    dayNumber: day,
    status: 'active',
    dailyBrief,
    audio: {
      status: dailyBrief.artifactStatuses.podcastAudio,
      url: null,
      durationSec: null,
      provider: null,
      format: null,
      updatedAt: null,
      error: null,
    },
    feedbackEvents: [],
  };
}

function normalizeLongevityDayPackage(dayPackage: LongevityDayPackage): LongevityDayPackage {
  const artifactStatuses = dayPackage.dailyBrief.artifactStatuses;
  const normalizedPodcastStatus = artifactStatuses.podcastAudio === 'pending'
    ? 'degraded'
    : artifactStatuses.podcastAudio;
  const audio = dayPackage.audio ?? {
    status: normalizedPodcastStatus,
    url: null,
    durationSec: null,
    provider: null,
    format: null,
    updatedAt: null,
    error: null,
  };
  return {
    ...dayPackage,
    audio: {
      ...audio,
      status: audio.status === 'pending' ? normalizedPodcastStatus : audio.status,
    },
    dailyBrief: {
      ...dayPackage.dailyBrief,
      artifactStatuses: {
        ...artifactStatuses,
        podcastAudio: audio.status === 'pending' ? normalizedPodcastStatus : audio.status,
        visualCardSet: artifactStatuses.visualCardSet === 'pending' || artifactStatuses.visualCardSet === 'processing'
          ? 'completed'
          : artifactStatuses.visualCardSet,
      },
    },
  };
}

function normalizeLongevityDaysResponse(response: LongevityPlanDaysResponse): LongevityPlanDaysResponse {
  return {
    ...response,
    days: response.days.map((day) => ({
      ...day,
      artifactStatuses: {
        ...day.artifactStatuses,
        podcastAudio: day.artifactStatuses.podcastAudio === 'pending' ? 'degraded' : day.artifactStatuses.podcastAudio,
        visualCardSet: day.artifactStatuses.visualCardSet === 'pending' || day.artifactStatuses.visualCardSet === 'processing'
          ? 'completed'
          : day.artifactStatuses.visualCardSet,
      },
    })),
  };
}

function mockLongevityDays(): LongevityPlanDaysResponse {
  const plan = mockPlanForGoals();
  return {
    plan,
    days: Array.from({ length: plan.durationDays }, (_, index) => {
      const dayNumber = index + 1;
      const brief = mockBriefForGoals(plan.selectedGoalCardIds, dayNumber);
      return {
        dayId: `mock-longevity-day-${dayNumber}`,
        dayNumber,
        status: dayNumber < plan.currentDay ? 'completed' : 'ready',
        title: brief.title,
        primaryGoalCardId: brief.primaryGoalCardId,
        taskTitle: brief.practiceTask.title,
        taskDoseLabel: brief.practiceTask.doseLabel,
        artifactStatuses: brief.artifactStatuses,
        latestFeedbackType: dayNumber < plan.currentDay ? 'done' : null,
        completedAt: dayNumber < plan.currentDay ? new Date().toISOString() : null,
      };
    }),
    progressDecisions: [],
  };
}

function mockPracticeSummary(): LongevityPracticeSummary {
  return {
    schemaVersion: 'longevity_practice_summary.v1',
    windowDays: 7,
    generatedAt: new Date().toISOString(),
    pillars: [
      {
        key: 'zone2',
        label: 'Zone 2 估算',
        value: 42,
        unit: '分钟/周',
        targetLabel: '先接近 150 分钟/周',
        status: 'building',
        source: 'wearable_workouts',
      },
      {
        key: 'zone5',
        label: '高强度次数',
        value: 0,
        unit: '次/周',
        targetLabel: '需要个体化确认',
        status: 'missing',
        source: 'wearable_workouts',
      },
      {
        key: 'strength',
        label: '力量刺激',
        value: 1,
        unit: '次/周',
        targetLabel: '先做到 2 次/周',
        status: 'building',
        source: 'wearable_or_manual',
      },
      {
        key: 'stability',
        label: '稳定性实践',
        value: 2,
        unit: '天/周',
        targetLabel: '先积累 3 天/周',
        status: 'building',
        source: 'manual_feedback',
      },
      {
        key: 'recovery',
        label: '睡眠恢复',
        value: 390,
        unit: '分钟/晚',
        targetLabel: '趋势观察，不作诊断',
        status: 'watch',
        source: 'wearable_sleep',
      },
    ],
    recovery: {
      status: 'watch',
      label: '恢复数据需要观察',
      summary: '近 7 天平均睡眠约 390 分钟；只用于调节实践强度。',
      sourceProviders: ['apple_health'],
      updatedAt: new Date().toISOString(),
    },
    providerStatuses: [
      {
        provider: 'apple_health',
        label: 'Apple Health',
        status: 'synced',
        lastSyncedAt: new Date().toISOString(),
        note: '已从已授权健康档案读取 Apple Health 同步数据。',
      },
      {
        provider: 'garmin',
        label: 'Garmin',
        status: 'ready_for_connector',
        lastSyncedAt: null,
        note: '已预留 provider-neutral 汇总表；真实连接需要开发者授权和用户 consent。',
      },
      {
        provider: 'manual',
        label: '手动记录',
        status: 'active',
        lastSyncedAt: null,
        note: '没有手表也可以用完成量、主观吃力程度、疼痛和原因完成闭环。',
      },
    ],
    manualFallbackAvailable: true,
    summary: '已汇总近 7 天可穿戴数据和手动反馈；这些数据只用于调整实践节奏，不替代医学诊断。',
  };
}

function mockWorkouts(): WorkoutSessionSummary[] {
  const now = new Date();
  const workoutStart = new Date(now);
  workoutStart.setHours(now.getHours() - 2);
  return [
    {
      id: 'mock-workout-cycling-001',
      activityType: 'cycling',
      activityName: '骑行',
      startedAt: workoutStart.toISOString(),
      endedAt: now.toISOString(),
      durationMinutes: 42,
      distanceKilometers: 18.4,
      energyKilocalories: 420,
      source: 'apple_health',
      contributionTags: ['心肺'],
    },
    {
      id: 'mock-workout-strength-001',
      activityType: 'strength_training',
      activityName: '力量训练',
      startedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      endedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000 + 28 * 60 * 1000).toISOString(),
      durationMinutes: 28,
      distanceKilometers: null,
      energyKilocalories: 160,
      source: 'apple_health',
      contributionTags: ['力量'],
    },
  ];
}

function mockPillarForAbilityTag(tag: string, summary: LongevityPracticeSummary) {
  const pillarKey = (() => {
    if (tag.includes('心肺') || tag.includes('步行') || tag.includes('耐力')) return 'zone2';
    if (tag.includes('力量') || tag.includes('推力') || tag.includes('抱举')) return 'strength';
    if (tag.includes('自我管理') || tag.includes('认知')) return 'recovery';
    return 'stability';
  })();
  return summary.pillars.find((pillar) => pillar.key === pillarKey) ?? summary.pillars[0];
}

function mockWorkoutForAbilityTag(tag: string, workouts: WorkoutSessionSummary[]) {
  const wanted = (() => {
    if (tag.includes('力量') || tag.includes('推力') || tag.includes('抱举')) return '力量';
    if (tag.includes('平衡') || tag.includes('稳定') || tag.includes('核心')) return '稳定性';
    if (tag.includes('心肺') || tag.includes('步行') || tag.includes('耐力')) return '心肺';
    return null;
  })();
  if (!wanted) return null;
  return workouts.find((workout) => workout.contributionTags.includes(wanted)) ?? null;
}

function mockAbilityLedger(): AbilityLedgerResponse {
  const summary = mockPracticeSummary();
  const workouts = mockWorkouts();
  const plan = mockPlanForGoals();
  return {
    schemaVersion: 'ability_ledger.v1',
    windowDays: 7,
    generatedAt: new Date().toISOString(),
    goals: plan.selectedGoalCardIds.slice(0, 3).map((goalCardId) => {
      const card = getGoalCardById(goalCardId);
      return {
        goalCardId,
        title: card?.title ?? '百岁目标',
        domains: (card?.abilityTags ?? ['心肺', '力量', '平衡']).slice(0, 4).map((tag, index) => {
          const pillar = mockPillarForAbilityTag(tag, summary);
          const workout = mockWorkoutForAbilityTag(tag, workouts);
          return {
            key: `${goalCardId}-${index}`,
            label: tag,
            status: pillar.status,
            progressLabel: `${pillar.label} 当前记录 ${pillar.value} ${pillar.unit}`,
            gapLabel: pillar.status === 'missing' ? `缺少支撑「${tag}」的连续证据` : null,
            evidence: [
              {
                sourceType: 'practice_summary',
                sourceLabel: pillar.source,
                valueLabel: `${pillar.value} ${pillar.unit}`,
                observedAt: summary.recovery.updatedAt,
                summary: pillar.targetLabel,
              },
              ...(workout ? [{
                sourceType: 'workout',
                sourceLabel: workout.activityName,
                valueLabel: `${workout.durationMinutes} 分钟`,
                observedAt: workout.endedAt,
                summary: '最近一次 workout 可作为客观实践证据。',
              }] : []),
            ],
          };
        }),
      };
    }),
    plannerBoundary: {
      allowedActions: ['解释当前安排', '记录完成反馈', '把 workout 作为完成证据'],
      blockedActions: ['自动增加高强度训练', '替代医学诊断', '读取 workout 路线/GPS'],
      doctorBoundary: '胸闷、晕厥、异常气短或明显疼痛时停止普通实践并线下就医。',
    },
    summary: '示例能力账本：把目标能力、近 7 天实践和 workout 证据合并展示。',
  };
}

function mockCycleReview(planId: string): CycleReviewResponse {
  return {
    schemaVersion: 'practice_cycle_review.v1',
    planId,
    windowDays: 7,
    generatedAt: new Date().toISOString(),
    completedDays: 4,
    workoutCount: mockWorkouts().length,
    summary: '本轮已完成 4 天反馈，读取 2 次 workout；下一轮优先补齐力量和稳定性。',
    keepDoing: ['每天一个低摩擦动作', '完成后记录主观吃力程度和疼痛', '保留 workout 作为客观证据'],
    adjustNext: ['下一轮先补力量刺激', '恢复不足时不加高强度'],
    dataGaps: ['缺少连续 HRV/恢复趋势', '缺少疼痛变化趋势'],
    safetyNotes: ['不根据单次 workout 自动升级强度', '安全风险线索进入问诊准备'],
  };
}

function mockWeeklyPracticePlan(): WeeklyPracticePlan {
  const plan = mockPlanForGoals();
  const summary = mockPracticeSummary();
  const primaryGoal = getGoalCardById(plan.selectedGoalCardIds[0] ?? '') ?? getGoalCardById(mockDailyBrief.primaryGoalCardId);
  if (!primaryGoal) throw new Error('百岁目标卡 mock 数据缺失');
  const mockWeekEnd = new Date(`${plan.startedAt}T00:00:00`);
  mockWeekEnd.setDate(mockWeekEnd.getDate() + plan.durationDays - 1);
  return {
    schemaVersion: 'practice_plan.v1',
    planId: plan.id,
    weekStart: plan.startedAt,
    weekEnd: plan.endDate ?? mockWeekEnd.toISOString().slice(0, 10),
    generatedAt: new Date().toISOString(),
    title: `首轮连续记录：先把“${primaryGoal.title}”做小、做稳`,
    summary: `28 天以“${primaryGoal.title}”为主线，每 7 天根据反馈调整剩余项目和强度；${summary.recovery.label}。${summary.summary}`,
    primaryGoal: {
      goalCardId: primaryGoal.id,
      title: primaryGoal.title,
      abilityTags: primaryGoal.abilityTags,
      practiceFamilies: primaryGoal.practiceFamilies,
    },
    pillars: summary.pillars
      .filter((pillar) => ['zone2', 'zone5', 'strength', 'stability', 'recovery'].includes(pillar.key))
      .map((pillar) => ({
        key: pillar.key,
        label: pillar.label,
        targetLabel: pillar.targetLabel,
        status: pillar.status,
        evidenceSource: pillar.source,
        planHint: mockWeeklyPillarHint(pillar.key, pillar.status),
      })),
    days: Array.from({ length: plan.durationDays }, (_, index) => {
      const dayNumber = index + 1;
      const brief = mockBriefForGoals(plan.selectedGoalCardIds, dayNumber);
      const task = brief.practiceTask;
      return {
        dayNumber,
        dayId: `mock-longevity-day-${dayNumber}`,
        status: dayNumber < plan.currentDay ? 'completed' : 'ready',
        focus: primaryGoal ? `${primaryGoal.title}：${primaryGoal.abilityTags[0] ?? '行动能力'}` : brief.title,
        plannedAdjustment: 'maintain',
        plannerNote: '按当前轻量剂量完成即可，完成后记录实际量、疲劳感和疼痛。',
        taskId: task.id,
        taskTitle: task.title,
        doseLabel: task.doseLabel,
        intensity: task.difficulty,
        estimatedMinutes: task.estimatedMinutes,
        whyToday: brief.oneThesis,
        fallbackTitle: task.alternativeTask.title,
        fallbackDoseLabel: task.alternativeTask.doseLabel,
        safetyStop: task.safetyStop,
        latestFeedbackType: dayNumber < plan.currentDay ? 'done' : null,
      };
    }),
    plannerDecision: {
      status: 'data_gap',
      label: '先补数据，不加量',
      rationale: '本地示例缺少真实连续数据；先用手动反馈补趋势，不用单次数据做强度判断。',
      dataGaps: ['高强度次数缺少近 7 天连续数据，先用手动完成量兜底。'],
      allowedActions: ['记录完成量', '记录疲劳和疼痛', '补同步可穿戴数据'],
      blockedActions: ['自动增加高强度训练', '用单次可穿戴数据下医学结论', '出现安全风险线索后继续普通打卡'],
    },
    adjustmentRules: [
      {
        trigger: '主观吃力程度 >= 8 或 painScore >= 4',
        action: 'downshift_next_day',
        summary: '下一天自动使用更小剂量或替代动作，避免硬扛。',
        requiresUserConfirmation: false,
      },
      {
        trigger: 'safety_signal_reported',
        action: 'doctor_visit_prep',
        summary: '停止普通实践，整理给医生沟通的问题和近期数据。',
        requiresUserConfirmation: true,
      },
    ],
    safetyBoundary: {
      redFlags: primaryGoal.safetyFlags,
      stopRules: [mockDailyBrief.safetyStop],
      doctorBoundary: mockDailyBrief.evidenceBoundary.doctorBoundary,
    },
    evidenceBoundary: mockDailyBrief.evidenceBoundary,
  };
}

function mockWeeklyPillarHint(key: string, status: string) {
  if (key === 'zone2') return status === 'missing' ? '先用低强度步行替代训练术语。' : '把低强度运动拆成更容易完成的小段。';
  if (key === 'zone5') return '高强度只做识别和边界确认，V1 不自动加量。';
  if (key === 'strength') return '先完成 2 次轻力量刺激，疼痛或主观吃力程度高时降级。';
  if (key === 'stability') return '把平衡、核心和关节控制安排成 3-5 分钟微任务。';
  return '恢复只作节奏调节，不作医学诊断。';
}

// ── API 调用 ──

export const executeApi = {
  /** 获取当前任务（后端已有统一接口） */
  async getTodayTasks(): Promise<DailyPlan> {
    const raw = await http.get<{
      tasks: Array<{
        id: string; title: string; description: string; category: string;
        completionMode: 'instant' | 'input'; inputUnit: string | null;
        isCompleted: boolean; source?: { id?: string; name?: string } | null;
      }>;
      summary: { total: number; completed: number; percent: number };
    }>('/tasks/today');

    return {
      date: new Date().toISOString().slice(0, 10),
      tasks: raw.tasks.map(t => ({
        id: t.id,
        title: t.title,
        description: t.description,
        tag: t.category,
        completed: t.isCompleted,
        completionMode: t.completionMode,
        inputUnit: t.inputUnit,
        courseId: t.source?.id ?? undefined,
      })),
      completedCount: raw.summary?.completed ?? 0,
      totalCount: raw.summary?.total ?? 0,
    };
  },

  /** 完成单个任务，input 模式需传 value */
  async completeTask(taskId: string, value?: string) {
    return http.post(`/tasks/${taskId}/complete`, value !== undefined ? { value } : {});
  },

  /** 获取连续打卡天数 */
  async getStreak(): Promise<TaskStreak> {
    return http.get<TaskStreak>('/tasks/streak');
  },

  /** 微光积累 */
  async getShimmer(): Promise<ShimmerData> {
    return http.get<ShimmerData>('/shimmer');
  },

  /** 执行计划列表 */
  async getPlans(): Promise<ExecutionPlan[]> {
    const res = await http.get<{ plans: ExecutionPlan[] }>('/plans');
    return res.plans ?? [];
  },

  /** 创建执行计划 */
  async createPlan(data: { title: string; description?: string; planType: string; targetDays: number }): Promise<ExecutionPlan> {
    return http.post<ExecutionPlan>('/plans', data);
  },

  /** 靶向逆转协议列表 */
  async getProtocols(condition?: string): Promise<Protocol[]> {
    const qs = condition ? `?condition=${encodeURIComponent(condition)}` : '';
    const res = await http.get<{ protocols: Protocol[] }>(`/protocols${qs}`);
    return res.protocols ?? [];
  },

  /** 食谱列表 */
  async getRecipes(category?: string, limit = 20): Promise<Recipe[]> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    params.set('limit', String(limit));
    const res = await http.get<{ recipes: Recipe[] }>(`/recipes?${params}`);
    return res.recipes ?? [];
  },

  /** 百岁目标卡库 */
  async getLongevityGoalCards(): Promise<LongevityGoalCard[]> {
    if (shouldUseMock('execute')) return longevityGoalCards;
    return http.get<LongevityGoalCard[]>('/longevity/goal-cards');
  },

  /** 百岁计划预览 */
  async previewLongevityPlan(data: {
    goalCardIds: string[];
    concern?: string;
    redFlags?: string[];
  }): Promise<LongevityPlanPreview> {
    if (shouldUseMock('execute')) {
      const goalCardIds = data.goalCardIds.length > 0
        ? data.goalCardIds
        : mockLongevityPlan.selectedGoalCardIds;
      const dailyBrief = mockBriefForGoals(goalCardIds);
      return {
        riskTriage: mockLongevityPlan.riskTriage,
        goalCards: goalCardIds
          .map((id) => getGoalCardById(id))
          .filter((card): card is LongevityGoalCard => Boolean(card)),
        evidenceBoundary: dailyBrief.evidenceBoundary,
        durationDays: 28,
        dailyBrief,
      };
    }
    return http.post<LongevityPlanPreview>('/longevity/plans/preview', data);
  },

  /** 创建百岁计划 */
  async createLongevityPlan(data: {
    goalCardIds: string[];
    personalMotives?: Record<string, string>;
    concern?: string;
    redFlags?: string[];
    durationDays?: number;
  }): Promise<LongevityDayPackage> {
    if (shouldUseMock('execute')) {
      return normalizeLongevityDayPackage(mockLongevityDayPackage(
        data.goalCardIds.length > 0 ? data.goalCardIds : mockLongevityPlan.selectedGoalCardIds,
      ));
    }
    return normalizeLongevityDayPackage(await http.post<LongevityDayPackage>('/longevity/plans', data));
  },

  /** 当前百岁计划 */
  async getActiveLongevityPlan(): Promise<LongevityPlan | null> {
    if (shouldUseMock('execute')) return mockPlanForGoals();
    return http.get<LongevityPlan | null>('/longevity/plans/active');
  },

  /** 百岁计划日课 */
  async getLongevityDay(planId: string, day: number): Promise<LongevityDayPackage> {
    if (shouldUseMock('execute')) {
      return normalizeLongevityDayPackage(mockLongevityDayPackage(mockLongevityPlan.selectedGoalCardIds, day));
    }
    return normalizeLongevityDayPackage(await http.get<LongevityDayPackage>(`/longevity/plans/${planId}/days/${day}`));
  },

  /** 生成百岁日课个性化播客 */
  async generateLongevityDayAudio(planId: string, day: number): Promise<GenerateLongevityAudioResponse> {
    if (shouldUseMock('execute')) {
      return {
        dayId: `mock-longevity-day-${day}`,
        dayNumber: day,
        audio: {
          status: 'processing',
          url: null,
          durationSec: null,
          provider: 'mimo',
          format: 'wav',
          updatedAt: new Date().toISOString(),
          error: null,
        },
      };
    }
    return http.post<GenerateLongevityAudioResponse>(`/longevity/plans/${planId}/days/${day}/audio`, {});
  },

  /** 百岁计划日课历史 */
  async getLongevityDays(planId: string): Promise<LongevityPlanDaysResponse> {
    if (shouldUseMock('execute')) return normalizeLongevityDaysResponse(mockLongevityDays());
    return normalizeLongevityDaysResponse(await http.get<LongevityPlanDaysResponse>(`/longevity/plans/${planId}/days`));
  },

  /** 百岁实践周方案 */
  async getLongevityWeeklyPlan(planId: string): Promise<WeeklyPracticePlan> {
    if (shouldUseMock('execute')) return mockWeeklyPracticePlan();
    return http.get<WeeklyPracticePlan>(`/longevity/plans/${planId}/weekly-plan`);
  },

  /** 百岁实践近 7 天数据汇总 */
  async getLongevityPracticeSummary(): Promise<LongevityPracticeSummary> {
    if (shouldUseMock('execute')) return mockPracticeSummary();
    return http.get<LongevityPracticeSummary>('/longevity/practice/summary');
  },

  /** 百岁能力账本 */
  async getLongevityAbilityLedger(): Promise<AbilityLedgerResponse> {
    if (shouldUseMock('execute')) return mockAbilityLedger();
    return http.get<AbilityLedgerResponse>('/longevity/ability-ledger');
  },

  /** 近 28 天 workout 摘要 */
  async getLongevityWorkouts(query: WorkoutSessionQuery = {}): Promise<WorkoutSessionSummary[]> {
    if (shouldUseMock('execute')) return mockWorkouts();
    const params = new URLSearchParams();
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.limit) params.set('limit', String(query.limit));
    if (query.activityType) params.set('activityType', query.activityType);
    const qs = params.toString();
    return http.get<WorkoutSessionSummary[]>(`/longevity/workouts${qs ? `?${qs}` : ''}`);
  },

  /** 单次 workout 详情 */
  async getLongevityWorkout(workoutId: string): Promise<WorkoutSessionDetail> {
    if (shouldUseMock('execute')) {
      const workout = mockWorkouts().find((item) => item.id === workoutId) ?? mockWorkouts()[0];
      return {
        summary: workout,
        metrics: [
          { key: 'duration', label: '运动时长', value: workout.durationMinutes, unit: 'min' },
          ...(workout.distanceKilometers ? [{ key: 'distance', label: '距离', value: workout.distanceKilometers, unit: 'km' }] : []),
          ...(workout.energyKilocalories ? [{ key: 'active_energy', label: '活动能量', value: workout.energyKilocalories, unit: 'kcal' }] : []),
        ],
        evidenceBoundary: {
          fromUserData: [`已读取 Apple Health workout：${workout.activityName}，约 ${workout.durationMinutes} 分钟。`],
          generalGuidance: [
            '这条记录只用于解释实践完成度和调节下一轮训练节奏。',
            '当前不读取 workout 路线/GPS，也不根据单次运动自动作医学结论。',
          ],
          missingData: ['缺少主观吃力程度、疼痛评分和当日恢复状态时，不自动上调强度。'],
          doctorBoundary: '出现胸闷、晕厥、异常气短、明显疼痛等安全风险线索时，停止普通实践并线下就医。',
        },
        rawSourceRef: { resourceType: 'DeviceMetric', key: 'activity.workout.session' },
      };
    }
    return http.get<WorkoutSessionDetail>(`/longevity/workouts/${encodeURIComponent(workoutId)}`);
  },

  /** 阶段实践复盘 */
  async getLongevityCycleReview(planId: string): Promise<CycleReviewResponse> {
    if (shouldUseMock('execute')) return mockCycleReview(planId);
    return http.get<CycleReviewResponse>(`/longevity/plans/${planId}/cycle-review`);
  },

  /** 滚动到下一轮实践 */
  async rolloverLongevityCycle(
    planId: string,
    data: CycleRolloverRequest = {},
  ): Promise<CycleRolloverResponse> {
    if (shouldUseMock('execute')) {
      return {
        previousPlanId: planId,
        nextPlanId: mockLongevityPlan.id,
        dayPackage: mockLongevityDayPackage(mockLongevityPlan.selectedGoalCardIds, 1),
        summary: '已基于上一轮复盘创建下一轮 Day 1。',
      };
    }
    return http.post<CycleRolloverResponse>(`/longevity/plans/${planId}/cycles/rollover`, data);
  },

  /** 调整百岁计划目标 */
  async adjustLongevityPlanGoals(
    planId: string,
    data: { goalCardIds: string[]; personalMotives?: Record<string, string> },
  ): Promise<LongevityPlan> {
    if (shouldUseMock('execute')) return mockPlanForGoals(data.goalCardIds);
    return http.post<LongevityPlan>(`/longevity/plans/${planId}/goals`, data);
  },

  /** 当前实践反馈 */
  async recordLongevityFeedback(
    taskId: string,
    feedbackType: FeedbackType,
    note?: string,
    completionPayload?: FeedbackCompletionPayload,
  ): Promise<LongevityFeedbackResponse> {
    if (shouldUseMock('execute')) {
      return {
        event: {
          id: `mock-feedback-${taskId}`,
          taskId,
          feedbackType,
          note,
          completionPayload,
          createdAt: new Date().toISOString(),
        },
        taskStatus: feedbackType === 'done' ? 'completed' : 'active',
        dayStatus: feedbackType === 'done' ? 'completed' : 'ready',
      };
    }
    return http.post(`/longevity/tasks/${taskId}/feedback`, {
      feedbackType,
      note,
      completionPayload,
    });
  },

  /** 关联 workout 或手动证据到实践任务 */
  async linkLongevityTaskEvidence(
    taskId: string,
    data: LinkPracticeEvidenceRequest,
  ): Promise<LinkPracticeEvidenceResponse> {
    if (shouldUseMock('execute')) {
      return {
        eventId: `mock-evidence-${taskId}`,
        taskId,
        planId: mockLongevityPlan.id,
        planDayId: 'mock-day-1',
        evidenceType: data.evidenceType,
        evidenceRefId: data.workoutId ?? null,
        taskStatus: data.completeTask ? 'completed' : 'active',
        dayStatus: data.completeTask ? 'completed' : 'ready',
        summary: data.evidenceType === 'workout_session' ? '已关联 1 次 workout。' : '已关联一条手动实践证据。',
      };
    }
    return http.post<LinkPracticeEvidenceResponse>(
      `/longevity/tasks/${encodeURIComponent(taskId)}/evidence`,
      data,
    );
  },

  /** 确认替换当前实践任务 */
  async replaceLongevityTask(taskId: string): Promise<PracticeTask> {
    if (shouldUseMock('execute')) {
      const currentTask = mockDailyBrief.practiceTask;
      return {
        ...currentTask,
        id: `${taskId}-lighter`,
        title: currentTask.alternativeTask.title,
        doseLabel: currentTask.alternativeTask.doseLabel,
        alternativeTask: {
          title: '靠墙推掌',
          doseLabel: '3 次 x 1 组',
        },
        status: 'active',
      };
    }
    return http.post<PracticeTask>('/longevity/tasks/' + encodeURIComponent(taskId) + '/replace', {
      confirmAlternative: true,
    });
  },

  /** 创建目标分享授权 */
  async shareLongevityGoal(goalId: string, data: {
    recipientType: 'family_member' | 'external_link';
    recipientRef: string;
    visibleFields?: string[];
    allowReshare?: boolean;
    expiresAt?: string | null;
  }): Promise<ShareGoalResponse> {
    if (shouldUseMock('execute')) {
      return {
        id: `mock-share-${goalId}`,
        status: 'active',
        visibleFields: data.visibleFields ?? ['goal_title', 'personal_motive'],
        allowReshare: data.allowReshare ?? false,
        expiresAt: data.expiresAt ?? null,
        createdAt: new Date().toISOString(),
        privacyBoundary: '默认不分享报告指标、任务反馈、家庭动机以外的敏感内容。',
      };
    }
    return http.post<ShareGoalResponse>(`/longevity/goals/${goalId}/share`, data);
  },
};
